/**
 * engine/scratch-protocol.ts — what the page and the scratch voice say to each other.
 *
 * Task 18. The voice lives in the audio thread (`engine/scratch-worklet.ts`), the hand and the
 * element on the page (`engine/scratch.ts`); a message port is all they share. Types and two
 * constants only, so the worklet's file carries nothing of the page with it.
 *
 * Seconds everywhere, never samples: the page does not know the rate the track was decoded at
 * nor the speaker's, and has no need to.
 */

/** The name the processor is registered under. */
export const SCRATCH_PROCESSOR = 'fdm-scratch';

/** The voice says where it is at most this often while it sounds — the slider's and the arm's pace. */
export const POSITION_EVERY_S = 0.05;

/**
 * …and where it is and how fast at most this often, a frame of the screen: the record is drawn
 * from the voice while the voice sounds, as the prototype drew it from its sound
 * (`components/Vinyl.tsx`). Its own message, so the slider is not rendered 60 times a second.
 */
export const MOTION_EVERY_S = 1 / 60;

/** From the page to the voice. */
export type ToVoice =
  /** The decoded track, one channel. The buffer is transferred, not copied. */
  | { readonly type: 'load'; readonly samples: Float32Array; readonly rate: number }
  | { readonly type: 'unload' }
  /** A hand on the record: from silence at `atS` and `rate`; a sounding voice where it is. */
  | { readonly type: 'hold'; readonly atS?: number; readonly rate?: number }
  /**
   * The finger's travel since the hand landed, in seconds of the track, and the time since its
   * report before, by the finger's own clock: the voice glides there over that time.
   */
  | { readonly type: 'follow'; readonly travelS: number; readonly spanS: number }
  | { readonly type: 'release'; readonly tempo: number }
  /** The element will start at `atS`. */
  | { readonly type: 'arrive'; readonly atS: number }
  | { readonly type: 'fade' }
  /**
   * Task 62: a loop from `startS` to `endS` at `rate` — from `atS`, or where the voice is; `roll`,
   * with the track going on under it (`lib/scratch-dsp.ts`, «A loop, a roll, a jump»).
   */
  | {
      readonly type: 'loop';
      readonly startS: number;
      readonly endS: number;
      readonly rate: number;
      readonly atS?: number;
      readonly roll?: boolean;
    }
  /** Task 62: the loop let go. */
  | { readonly type: 'unloop' }
  /** Task 62: BEAT JUMP of a looping voice — it and its loop, `byS` of the track. */
  | { readonly type: 'jump'; readonly byS: number };

/** From the voice to the page. */
export type FromVoice =
  | { readonly type: 'position'; readonly atS: number }
  /** Where the voice is, in seconds of the track, and its speed in track seconds per second. */
  | { readonly type: 'motion'; readonly atS: number; readonly rate: number }
  /** The motor is at the tempo, at `atS`. */
  | { readonly type: 'steady'; readonly atS: number }
  /** The voice is where the element starts, and fading. */
  | { readonly type: 'arrived'; readonly atS: number }
  | { readonly type: 'silent' };
